import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, LogOut, CalendarDays, Users, ChevronDown, ChevronUp, Mail, Phone, Newspaper } from "lucide-react";
import AdminNewsletterTab from "@/components/AdminNewsletterTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState("events");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [eventForm, setEventForm] = useState({ title: "", description: "", date: "", time: "", location: "", recurring: "none", recurring_end_date: "" });
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [registrations, setRegistrations] = useState({});

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events/all`, { withCredentials: true });
      setEvents(data);
    } catch (err) {
      console.error("Events fetch error:", err);
    }
  }, []);

  const fetchRegistrations = useCallback(async (eventId) => {
    try {
      const { data } = await axios.get(`${API}/events/${eventId}/registrations`, { withCredentials: true });
      setRegistrations(prev => ({ ...prev, [eventId]: data }));
    } catch (err) {
      console.error("Registrations fetch error:", err);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => { if (user) fetchEvents(); }, [user, fetchEvents]);

  const toggleExpand = (eventId) => {
    if (expandedEvent === eventId) {
      setExpandedEvent(null);
    } else {
      setExpandedEvent(eventId);
      if (!registrations[eventId]) fetchRegistrations(eventId);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: loginForm.email, password: loginForm.password },
        { withCredentials: true }
      );
      setUser(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Login fehlgeschlagen");
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const openCreateDialog = () => {
    setEditing(null);
    setEventForm({ title: "", description: "", date: "", time: "", location: "", recurring: "none", recurring_end_date: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setEditing(event);
    setEventForm({
      title: event.title,
      description: event.description || "",
      date: event.date,
      time: event.time || "",
      location: event.location || "",
      recurring: "none",
      recurring_end_date: "",
    });
    setDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/events/${editing.id || editing._id}`, eventForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/events`, eventForm, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (err) {
      console.error("Save event error:", err);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Veranstaltung wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      fetchEvents();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0C1620] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#0C1620] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl text-white mb-2">Admin Login</h1>
            <p className="text-[#8899AA] text-sm">Veranstaltungskalender verwalten</p>
          </div>
          <form onSubmit={handleLogin} className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">E-Mail</label>
              <Input data-testid="admin-email-input" type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" />
            </div>
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">Passwort</label>
              <Input data-testid="admin-password-input" type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" />
            </div>
            {loginError && <p data-testid="admin-login-error" className="text-red-400 text-sm">{loginError}</p>}
            <Button data-testid="admin-login-button" type="submit" className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">Anmelden</Button>
          </form>
          <div className="text-center mt-6">
            <Link to="/" className="text-[#8899AA] hover:text-[#C5993A] text-sm transition-colors">
              <ArrowLeft className="inline w-4 h-4 mr-1" /> Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0C1620]">
      <header className="bg-[#0C1620]/80 backdrop-blur-xl border-b border-[#3A4F60]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/" className="text-[#8899AA] hover:text-[#C5993A] transition-colors"><ArrowLeft className="w-4 h-4" /></Link>
            <h1 className="font-heading text-lg sm:text-xl text-white">Admin</h1>
          </div>
          <Button data-testid="admin-logout-button" onClick={handleLogout} variant="ghost" className="text-[#8899AA] hover:text-[#C5993A] text-sm">
            <LogOut className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Abmelden</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-[#3A4F60]/30 pb-4">
          <Button
            onClick={() => setActiveTab("events")}
            variant={activeTab === "events" ? "default" : "ghost"}
            className={activeTab === "events" ? "bg-[#C5993A] text-white border-0" : "text-[#8899AA]"}
          >
            <CalendarDays className="w-4 h-4 mr-2" /> Veranstaltungen
          </Button>
          <Button
            onClick={() => setActiveTab("newsletter")}
            variant={activeTab === "newsletter" ? "default" : "ghost"}
            className={activeTab === "newsletter" ? "bg-[#C5993A] text-white border-0" : "text-[#8899AA]"}
          >
            <Newspaper className="w-4 h-4 mr-2" /> Newsletter
          </Button>
        </div>

        {activeTab === "newsletter" ? (
          <AdminNewsletterTab />
        ) : (
        <>
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-heading text-2xl text-white">Alle Veranstaltungen</h2>
          <Button data-testid="create-event-button" onClick={openCreateDialog} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">
            <Plus className="w-4 h-4 mr-2" /> Neue Veranstaltung
          </Button>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-16 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg">
            <CalendarDays className="w-12 h-12 text-[#3A4F60] mx-auto mb-4" />
            <p className="text-[#8899AA]">Noch keine Veranstaltungen vorhanden.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-events-list">
            {events.map((event) => {
              const eid = event.id || event._id;
              const isExpanded = expandedEvent === eid;
              const regs = registrations[eid] || [];
              const confirmedCount = regs.filter(r => r.status !== "cancelled").length;

              return (
                <div key={eid} data-testid={`admin-event-${eid}`} className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg overflow-hidden">
                  <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{event.title}</h3>
                      <div className="flex flex-wrap gap-3 mt-1 text-sm text-[#8899AA]">
                        <span>{new Date(event.date + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                        {event.time && <span>{event.time}</span>}
                        {event.location && <span>{event.location}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        data-testid={`toggle-regs-${eid}`}
                        onClick={() => toggleExpand(eid)}
                        variant="ghost"
                        size="sm"
                        className="text-[#8899AA] hover:text-[#C5993A] gap-1"
                      >
                        <Users className="w-4 h-4" />
                        <span className="text-xs">{confirmedCount > 0 ? confirmedCount : ""}</span>
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </Button>
                      <Button data-testid={`edit-event-${eid}`} onClick={() => openEditDialog(event)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A]">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`delete-event-${eid}`} onClick={() => handleDeleteEvent(eid)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Registrations list */}
                  {isExpanded && (
                    <div data-testid={`regs-list-${eid}`} className="border-t border-[#3A4F60]/20 bg-[#0C1620]/50 p-5">
                      <h4 className="text-sm font-medium text-[#C5993A] mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Anmeldungen
                      </h4>
                      {regs.length === 0 ? (
                        <p className="text-[#3A4F60] text-sm">Keine Anmeldungen vorhanden.</p>
                      ) : (
                        <div className="space-y-2">
                          {regs.map((reg, i) => (
                            <div
                              key={reg.id || i}
                              className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 rounded-lg border ${
                                reg.status === "cancelled"
                                  ? "border-red-400/20 bg-red-400/5"
                                  : "border-[#3A4F60]/20 bg-[#12202f]"
                              }`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium truncate">
                                  {reg.name}
                                  {reg.status === "cancelled" && (
                                    <span className="ml-2 text-xs text-red-400 font-normal">storniert</span>
                                  )}
                                </p>
                                <div className="flex flex-wrap gap-3 text-xs text-[#8899AA] mt-1">
                                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{reg.email}</span>
                                  {reg.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{reg.phone}</span>}
                                </div>
                              </div>
                              <span className="text-[#3A4F60] text-xs flex-shrink-0">
                                {new Date(reg.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-lg" aria-describedby="event-dialog-desc">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Veranstaltung bearbeiten" : "Neue Veranstaltung"}</DialogTitle>
            <p id="event-dialog-desc" className="sr-only">Veranstaltungsformular</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">Titel *</label>
              <Input data-testid="event-title-input" required value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" />
            </div>
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">{!editing && eventForm.recurring !== "none" ? "Startdatum *" : "Datum *"}</label>
              <Input data-testid="event-date-input" type="date" required value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#8899AA] mb-1.5">Uhrzeit</label>
                <Input data-testid="event-time-input" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} placeholder="z.B. 19:00 - 21:00" className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" />
              </div>
              <div>
                <label className="block text-sm text-[#8899AA] mb-1.5">Ort</label>
                <Input data-testid="event-location-input" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="z.B. München" className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">Beschreibung</label>
              <Textarea data-testid="event-description-input" value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} rows={3} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A] resize-none" />
            </div>
            {/* Recurring event options - only for new events */}
            {!editing && (
              <div className="border-t border-[#3A4F60]/30 pt-4 space-y-4">
                <div>
                  <label className="block text-sm text-[#8899AA] mb-1.5">Wiederholung</label>
                  <select
                    data-testid="event-recurring-select"
                    value={eventForm.recurring}
                    onChange={(e) => setEventForm({ ...eventForm, recurring: e.target.value })}
                    className="w-full h-10 rounded-md border border-[#3A4F60]/50 bg-[#0C1620] text-white px-3 text-sm focus:border-[#C5993A] focus:outline-none"
                  >
                    <option value="none">Einmalig</option>
                    <option value="weekly">Wöchentlich</option>
                    <option value="biweekly">Alle 2 Wochen</option>
                    <option value="monthly">Monatlich</option>
                  </select>
                </div>
                {eventForm.recurring !== "none" && (
                  <div>
                    <label className="block text-sm text-[#8899AA] mb-1.5">Serie endet am *</label>
                    <Input
                      data-testid="event-recurring-end-input"
                      type="date"
                      required
                      value={eventForm.recurring_end_date}
                      onChange={(e) => setEventForm({ ...eventForm, recurring_end_date: e.target.value })}
                      className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]"
                    />
                    <p className="text-xs text-[#3A4F60] mt-1">
                      Es werden einzelne Termine vom Startdatum bis zu diesem Datum erstellt.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="cancel-event-button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#8899AA]">Abbrechen</Button>
            <Button data-testid="save-event-button" onClick={handleSaveEvent} disabled={!eventForm.title || !eventForm.date || (!editing && eventForm.recurring !== "none" && !eventForm.recurring_end_date)} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">{editing ? "Aktualisieren" : (eventForm.recurring !== "none" ? "Serie erstellen" : "Erstellen")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
